import { useMutation, useQueryClient } from "@tanstack/react-query";
import { affiliateCodeKeys } from "Api/Queries/Affiliate/useAffiliateReferrerQuery";
import { LICENSE_QUERY_KEY } from "Api/Queries/Licenses/useLicenseQuery";
import { partyLicensesKeys } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { transactionsDetailKeys } from "Api/Queries/Transactions/useTransactionsDetail";
import {
  type AffiliateReferrerDto,
  postIdentitiesRolesIdentityUserPublicIDAffiliateCode,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Variables = {
  identityUserPublicID: string;
  code: string;
};

type Props = {
  onSuccess?: (result: AffiliateReferrerDto) => void;
  onError?: (error: unknown) => void;
};

/**
 * Attaches an affiliate code to the caller's Onboarding Hunter role (license step). The license
 * offer switches to the affiliate price and an unpaid order is re-priced, so the offer, the orders
 * and the order's payment details are refetched.
 */
export const useAttachHunterAffiliateCodeMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ identityUserPublicID, code }: Variables) =>
        postIdentitiesRolesIdentityUserPublicIDAffiliateCode(
          { code },
          identityUserPublicID,
        ),
    ),
    onSuccess: (result: AffiliateReferrerDto) => {
      queryClient.invalidateQueries({ queryKey: affiliateCodeKeys.all });
      queryClient.invalidateQueries({ queryKey: [LICENSE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: partyLicensesKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionsDetailKeys.all });
      onSuccess?.(result);
    },
    onError,
  });
};
